<template>
  <div class="instructions">
    <div class="ctas">
      <div class="cta-wrapper">
        <div class="cta-wrapper__title">
          <strong>
            J'ai un ticket papier
          </strong>
          <InformationOutlineSvg
            :height="20"
            :color="paletteFidmarques.v2.blue"
            class="cta-wrapper__title__i"
            @click.native.stop="showTooltipPaper = !showTooltipPaper"
          />
          <div
            v-show="showTooltipPaper"
            v-click-outside="closeTooltipPaper"
            class="cta-wrapper__title__tooltip cta-wrapper__title__tooltip--left"
            @click="closeTooltipPaper"
          >
            <div>
              Avant de prendre la photo
              <ul>
                <li>
                  Je n'ai pas écrit ni surligné sur le ticket de caisse
                </li>
                <li>
                  Mon ticket est placé sur un fond de couleur uni et sombre
                </li>
              </ul>
            </div>
            <div class="divider--border-bottom-white" />
            <div>
              Ce que doit montrer la photo
              <ul>
                <li>Le nom et l'adresse du magasin</li>
                <li>La date et l'heure d'achat</li>
                <li>Les produits éligibles (nom, quantité et prix)</li>
              </ul>
            </div>
          </div>
        </div>

        <div
          v-if="isUploadingPaper"
          class="cta-wrapper__btn cta-wrapper__btn--disabled"
        >
          <LoaderDualRings
            class="button__loader"
            :total-size="5"
            :internal-ring-size="2"
            :external-ring-size="3"
          />
        </div>
        <label
          v-else
          class="cta-wrapper__btn"
          for="receipt-paper"
        >
          <CameraSvg :height="30" />
        </label>
        <input
          id="receipt-paper"
          name="receipt-paper"
          type="file"
          accept="image/*"
          capture="environment"
          class="cta-wrapper__input"
          @input="uploadPaper"
        >
      </div>
      <div class="cta-wrapper">
        <div class="cta-wrapper__title">
          <strong>
            J'ai un ticket digital
          </strong>
          <InformationOutlineSvg
            :height="20"
            :color="paletteFidmarques.v2.blue"
            class="cta-wrapper__title__i"
            @click.native.stop="showTooltipDigital = !showTooltipDigital"
          />
          <div
            v-show="showTooltipDigital"
            v-click-outside="closeTooltipDigital"
            class="cta-wrapper__title__tooltip cta-wrapper__title__tooltip--right"
            @click="closeTooltipDigital"
          >
            <ul>
              <li>
                Ma facture concerne des achats en ligne en grande distribution (Auchan, Intermarché, ...)
                et pas sur des marketplaces ou services de livraison (Amazon, UberEats, ...)
              </li>
              <li>
                J’utilise ma facture d’achat si j’ai commandé par le drive
                (une confirmation de commande ne pourra pas être validée)
              </li>
            </ul>
          </div>
        </div>

        <div
          v-if="isUploadingDigital"
          class="cta-wrapper__btn cta-wrapper__btn--disabled"
        >
          <LoaderDualRings
            class="button__loader"
            :total-size="5"
            :internal-ring-size="2"
            :external-ring-size="3"
          />
        </div>
        <label v-else class="cta-wrapper__btn" for="receipt-digital">
          <FilePdfSvg :height="36" />
        </label>
        <input
          id="receipt-digital"
          name="receipt-digital"
          type="file"
          accept="application/pdf"
          class="cta-wrapper__input"
          @input="uploadDigital"
        >

        <div v-show="$route.query.isMobile === 't'" class="cta-wrapper__subtitle">
          sur mon téléphone
        </div>
      </div>
    </div>
    <div v-if="emailForDigitalReceipts" class="text--email">
      ou j'envoie mon ticket digital à <br>
      <a :href="`mailto:${emailForDigitalReceipts}`">
        {{ emailForDigitalReceipts }}
      </a>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

import CameraSvg from '@/assets/images/camera.svg'
import FilePdfSvg from '@/assets/images/file-pdf.svg'
import InformationOutlineSvg from '@/assets/images/information-outline.svg'

import LoaderDualRings from '~/components/loaders/DualRings'

import MixinPaletteFidmarques from '~/mixins/paletteFidmarques'

export default {
  components: {
    CameraSvg,
    FilePdfSvg,
    InformationOutlineSvg,
    LoaderDualRings,
  },
  mixins: [MixinPaletteFidmarques],
  data: () => ({
    showTooltipPaper: false,
    showTooltipDigital: false,

    isUploadingPaper: false,
    isUploadingDigital: false,
    isUploading: false,
    uploadPercentage: 0,
  }),
  computed: {
    ...mapState('server', ['emailForDigitalReceipts']),
  },
  watch: {
    showTooltipPaper (val) {
      if (val) {
        this.showTooltipDigital = false
      }
    },
    showTooltipDigital (val) {
      if (val) {
        this.showTooltipPaper = false
      }
    },
  },
  methods: {
    ...mapActions(['addToast']),

    closeTooltipPaper () {
      this.showTooltipPaper = false
    },
    closeTooltipDigital () {
      this.showTooltipDigital = false
    },

    uploadPaper ($event) {
      if (this.isUploadingPaper) { return Promise.resolve() }

      this.isUploadingPaper = true
      return this.checkFilesAndUploadThem($event).finally(() => {
        this.isUploadingPaper = false
      })
    },
    uploadDigital ($event) {
      if (this.isUploadingDigital) { return Promise.resolve() }

      this.isUploadingDigital = true
      return this.checkFilesAndUploadThem($event).finally(() => {
        this.isUploadingDigital = false
      })
    },

    checkFilesAndUploadThem ($event) {
      let error
      for (let i = 0; i < $event.target.files.length; i++) {
        const file = $event.target.files[i]
        if (file.size / 1000 / 1000 > 10) {
          error = 'Fichier trop large (10 Mo max)'
        } else if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
          error = 'Type de fichier non pris en charge'
        }
      }

      if (error) {
        this.addToast({
          text: error,
          type: 'danger',
          dismissAfter: 4000,
        })
        return Promise.resolve()
      }

      return this.uploadFiles($event.target.files).finally(() => {
        this.resetInput($event.target)
      })
    },
    uploadFiles (files) {
      if (this.isUploading) { return Promise.resolve() }

      this.isUploading = true
      this.uploadPercentage = 0
      return this.$PE.client.uploadWithFiles({
        files,
        onProgress: (percentage) => { this.uploadPercentage = Math.round(percentage) },
      })
        .then(
          (_uuids) => {
            this.addToast({
              text: 'Votre ticket a bien été envoyé.',
              type: 'success',
              dismissAfter: 4000,
            })
            this.$store.dispatch('timeline/reset')
          },
          (error) => {
            this.$airbrake.notifyFromKit(error, { locator: 'shoot insctructions' }, { store: this.$store })
            this.addToastGenericError()
          },
        )
        .finally(() => {
          this.isUploading = false
        })
    },

    resetInput (input) {
      input.value = ''
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~assets/css/variables";

.instructions {
  background-color: white;
  padding: 5px;
  margin-bottom: 5px;
}
.ctas {
  display: flex;
  font-size: 12px;
}
.cta-wrapper {
  position: relative;
  flex: 1;
}
.cta-wrapper + .cta-wrapper {
  margin-left: 10px;
}

.cta-wrapper__title {
  position: relative;
}
.cta-wrapper__title__i {
  vertical-align: middle;
  cursor: pointer;
}
.cta-wrapper__subtitle {
  text-align: center;
  color: $color-text-gray-dark;
}
.cta-wrapper__title__tooltip {
  z-index: 10;
  position: absolute;
  top: 100%;

  margin-top: 5px;
  width: calc(100vw - 20px);
  padding: 5px 10px;
  border-radius: 10px;

  color: white;
  background-color: $palette-fidmarques-v2-blue;
}
.cta-wrapper__title__tooltip--left {
  left: 0;
}
.cta-wrapper__title__tooltip--right {
  right: 0;
}

$height: 50px;
.cta-wrapper__btn {
  margin: 5px 0;
  height: $height;
  border-radius: $height / 2;
  background-color: $palette-fidmarques-fuchsia;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 0.2rem 0.2rem rgba(0, 0, 0, 0.5);
}
.cta-wrapper__btn--disabled {
  opacity: 0.65;
  cursor: default;
  cursor: not-allowed;
}

.cta-wrapper__input {
  opacity: 0;
  position: absolute;
  height: 0;
  width: 0;
}

.divider--border-bottom-white {
  border-bottom: 1px solid white;
  margin-top: 10px;
  margin-bottom: 10px;
}
.text--email {
  color: $color-text-gray-dark;
  margin-top: 10px;
}
</style>
